import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { useRecoilCallback } from 'recoil';
import fragments from 'services/fragments';
import { Account } from 'types/Account';
import { ServiceOrder, SummaryServiceOrder } from 'types/serviceOrder.types';
import { serviceOrdersAtom } from './serviceOrdersPage.atoms';
import useActionsParams from './useActionsParams';

const SERVICE_ORDER_LIST = gql`
  query ServiceOrderList(
    $isCloud: Boolean!
    $masterCompanyId: Int!
    $accountId: Int!
    $hasPermissionToViewOpenSO: Boolean!
    $timezone: Int!
    $email: String!
    $beginDate: String!
    $endDate: String!
  ) {
    getServiceOrdersList(
      isCloud: $isCloud
      masterCompanyId: $masterCompanyId
      accountId: $accountId
      hasPermissionToViewOpenSO: $hasPermissionToViewOpenSO
      timezone: $timezone
      email: $email
      beginDate: $beginDate
      endDate: $endDate
    ) {
      id
      serviceOrderId
      sequential
      type
      date
      endDate
      hasOSImages
      defect
      description
      scheduleDate
      executant
      solution
      cause
      status
    }
  }
`;

const SERVICE_ORDER_ACCOUNTS_OVERVIEW = gql`
  ${fragments.ServiceOrders}
  query GetAccountsOverviewServiceOrders($beginDate: String!, $endDate: String!) {
    getAccountsOverviewServiceOrders(beginDate: $beginDate, endDate: $endDate) {
      ...ServiceOrderProperties
    }
  }
`;

type ServiceOrderListGQL = {
  getServiceOrdersList: ServiceOrder[];
};
type ServiceOrderAccount = {
  getAccountsOverviewServiceOrders: SummaryServiceOrder[];
};

function useServiceOrdersData({ accountSelected }: { accountSelected: Account | null }) {
  const { beginDate, endDate } = useActionsParams();
  const { data, error, loading, refetch } = useQuery<ServiceOrderListGQL | ServiceOrderAccount>(
    accountSelected ? SERVICE_ORDER_LIST : SERVICE_ORDER_ACCOUNTS_OVERVIEW,
    {
      variables: {
        ...accountSelected,
        email: accountSelected?.email,
        accountId: accountSelected?.id,
        beginDate,
        endDate,
      },
    },
  );

  const listData = React.useMemo(() => {
    if (data) {
      return (
        (data as ServiceOrderListGQL).getServiceOrdersList ||
        (data as ServiceOrderAccount).getAccountsOverviewServiceOrders
      );
    }
  }, [data]);

  const setData = useRecoilCallback(
    ({ set }) =>
      (newData: ServiceOrder[] | SummaryServiceOrder[] | undefined) => {
        set(serviceOrdersAtom, newData);
      },
  );

  React.useEffect(
    function setEventsWhenChangeData() {
      setData(listData);
    },
    [data, listData, setData],
  );

  if (error) {
    throw error;
  }

  return {
    data: listData,
    loading,
    refetch,
  };
}

export default useServiceOrdersData;
